<template>
  <div class="gcal w-100">
    <div class="gcal__toolbar">
      <div class="d-flex align-items-center">
        <button class="gcal__today" @click="goToday">Hôm nay</button>
        <button class="gcal__nav" title="Tháng trước" @click="changeMonth(-1)">
          <i class="fas fa-chevron-left"></i>
        </button>
        <button class="gcal__nav" title="Tháng sau" @click="changeMonth(1)">
          <i class="fas fa-chevron-right"></i>
        </button>
        <h2 class="gcal__title">{{ monthLabel }}</h2>
      </div>

      <div class="gcal__filter">
        <basic-select
          class="mb-0"
          name="campaign"
          placeholder="Tất cả chiến dịch"
          :options="campaignOptions"
          :value.sync="selectedCampaign"
          value-label="text"
          track-by="value"
        />
      </div>
    </div>

    <b-overlay :show="loading" rounded="sm">
      <div class="gcal__frame">
        <div class="gcal__weekdays">
          <div v-for="d in weekDays" :key="d" class="gcal__weekday">{{ d }}</div>
        </div>

        <div class="gcal__grid">
          <div
            v-for="cell in cells"
            :key="cell.key"
            class="gcal__cell"
            :class="{ 'is-outside': !cell.inMonth }"
          >
            <div class="gcal__daynum-wrap">
              <span class="gcal__daynum" :class="{ 'is-today': cell.isToday }">
                {{ cell.day }}
              </span>
            </div>

            <div class="gcal__events">
              <button
                v-for="p in cell.partners"
                :key="p.scheduleDayId"
                class="gcal__chip"
                :class="chipClass(p)"
                :title="`${p.partnerName || 'Không rõ'} — ${p.bookedCount}/${p.totalCapacity} lượt đăng ký`"
                @click="openDetail(cell, p)"
              >
                <span class="gcal__dot"></span>
                <span class="gcal__chip-name">{{ p.partnerName || 'Không rõ' }}</span>
                <span class="gcal__chip-count">{{ p.bookedCount }}/{{ p.totalCapacity }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </b-overlay>

    <b-modal v-model="showDetail" size="lg" hide-footer :title="detailTitle">
      <b-overlay :show="loadingDetail" rounded="sm">
        <div class="gcal__detail">
          <table class="table table-vertical-center mb-0">
            <thead>
              <tr>
                <th>Tên khách</th>
                <th style="width: 130px">Số điện thoại</th>
                <th>Email</th>
                <th>Công ty</th>
                <th style="width: 110px">Giờ khám</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in detailRows" :key="i">
                <td>{{ row.fullName }}</td>
                <td>{{ formatPhone(row.phone) }}</td>
                <td>{{ row.email || '—' }}</td>
                <td>{{ row.companyName || '—' }}</td>
                <td>{{ formatTime(row.startTime) }}</td>
              </tr>
              <tr v-if="!loadingDetail && !detailRows.length">
                <td colspan="5" class="text-center text-muted py-6">
                  Chưa có khách nào đăng ký
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'BcbExamCalendar',
  data() {
    const now = new Date();
    return {
      loading: false,
      loadingDetail: false,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      days: [],
      campaignOptions: [],
      selectedCampaign: null,
      weekDays: ['TH 2', 'TH 3', 'TH 4', 'TH 5', 'TH 6', 'TH 7', 'CN'],
      showDetail: false,
      detailRows: [],
      detailTitle: '',
    };
  },
  computed: {
    monthLabel() {
      return `Tháng ${this.month} năm ${this.year}`;
    },
    dayMap() {
      const map = {};
      this.days.forEach((d) => {
        map[this.apiDateKey(d.examDate)] = d.partners || [];
      });
      return map;
    },
    // Lưới 6 tuần cố định, tuần bắt đầu từ Thứ 2 như lịch Việt Nam.
    cells() {
      const first = new Date(this.year, this.month - 1, 1);
      const offset = (first.getDay() + 6) % 7;
      const start = new Date(this.year, this.month - 1, 1 - offset);
      const todayKey = this.dateKey(new Date());

      return Array.from({ length: 42 }, (_, i) => {
        const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
        const key = this.dateKey(date);
        return {
          key,
          date,
          day: date.getDate(),
          inMonth: date.getMonth() === this.month - 1,
          isToday: key === todayKey,
          partners: this.dayMap[key] || [],
        };
      });
    },
  },
  // Theo dõi trực tiếp thay vì bắt sự kiện của select: dùng @update:value cùng lúc
  // với :value.sync thì handler có thể chạy trước khi biến kịp cập nhật.
  watch: {
    selectedCampaign() {
      this.loadCalendar();
    },
  },
  created() {
    this.loadCampaigns();
    this.loadCalendar();
  },
  methods: {
    dateKey(date) {
      const m = `${date.getMonth() + 1}`.padStart(2, '0');
      const d = `${date.getDate()}`.padStart(2, '0');
      return `${date.getFullYear()}-${m}-${d}`;
    },
    // API trả ngày dạng unix seconds (số), một số chỗ trả chuỗi ISO - nhận cả hai.
    apiDateKey(value) {
      if (value === null || value === undefined) return '';
      const m =
        typeof value === 'number'
          ? this.$moment.unix(value).utcOffset('+07:00')
          : this.$moment(value).utcOffset('+07:00');
      return m.format('YYYY-MM-DD');
    },
    chipClass(partner) {
      if (!partner.totalCapacity) return 'is-empty';
      if (partner.bookedCount >= partner.totalCapacity) return 'is-full';
      if (partner.bookedCount / partner.totalCapacity >= 0.8) return 'is-nearly-full';
      return '';
    },
    formatTime(value) {
      // BE trả TimeSpan dạng "HH:mm:ss", chỉ hiển thị giờ và phút.
      return value ? String(value).slice(0, 5) : '';
    },
    // SĐT lưu chuẩn hoá dạng +84..., hiển thị lại dạng 0... cho dễ đọc.
    formatPhone(value) {
      if (!value) return '—';
      return String(value).replace(/^\+84/, '0');
    },
    formatDate(date) {
      const d = `${date.getDate()}`.padStart(2, '0');
      const m = `${date.getMonth() + 1}`.padStart(2, '0');
      return `${d}/${m}/${date.getFullYear()}`;
    },
    changeMonth(step) {
      const d = new Date(this.year, this.month - 1 + step, 1);
      this.year = d.getFullYear();
      this.month = d.getMonth() + 1;
      this.loadCalendar();
    },
    goToday() {
      const now = new Date();
      this.year = now.getFullYear();
      this.month = now.getMonth() + 1;
      this.loadCalendar();
    },
    async loadCampaigns() {
      try {
        const { meta, data } = await this.$api.get('/BcbCampaign', {
          params: { page: 1, size: 1000 },
        });
        if (!meta.success) return;
        this.campaignOptions = (data.items || []).map((el) => ({
          text: el.name,
          value: el.id,
        }));
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi', msg: error.message });
      }
    },
    async loadCalendar() {
      try {
        this.loading = true;
        const params = { year: this.year, month: this.month };
        if (this.selectedCampaign) params.campaignId = this.selectedCampaign;

        const { meta, data, error } = await this.$api.get(
          '/BcbPartnerScheduleDay/calendar',
          { params },
        );
        if (!meta.success) {
          return this.$toastr.e({ title: 'Lỗi', msg: error.message });
        }
        this.days = data || [];
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi', msg: error.message });
      } finally {
        this.loading = false;
      }
    },
    async openDetail(cell, partner) {
      this.showDetail = true;
      this.detailRows = [];
      this.detailTitle = `${partner.partnerName || 'Không rõ'} — ${this.formatDate(cell.date)}`;

      try {
        this.loadingDetail = true;
        // Model binder của framework chỉ nhận unix timestamp; cộng 7 giờ theo đúng
        // quy ước đang dùng ở các màn khác để BE nhận về đúng ngày giờ Việt Nam.
        const params = {
          partnerId: partner.partnerId,
          examDate: this.convertDateToTimestamp(cell.key, 'YYYY-MM-DD') + 7 * 3600,
        };
        if (this.selectedCampaign) params.campaignId = this.selectedCampaign;

        const { meta, data, error } = await this.$api.get(
          '/BcbPartnerScheduleDay/calendar/customers',
          { params },
        );
        if (!meta.success) {
          return this.$toastr.e({ title: 'Lỗi', msg: error.message });
        }
        this.detailRows = data || [];
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi', msg: error.message });
      } finally {
        this.loadingDetail = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$line: #dadce0;
$text: #3c4043;
$muted: #70757a;
$accent: #01645a;      // success cua he thong ($green-500)
$accent-light: #c9f7f5; // success-light
$accent-active: #a5edea;

.gcal {
  font-family: Roboto, Arial, sans-serif;
  color: $text;

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 8px 0 16px;
    flex-wrap: wrap;
  }

  &__today {
    height: 36px;
    padding: 0 16px;
    margin-right: 16px;
    border: 1px solid $line;
    border-radius: 4px;
    background: #fff;
    font-size: 14px;
    color: $text;

    &:hover {
      background: #f1f3f4;
    }
  }

  &__nav {
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: $muted;

    &:hover {
      background: #f1f3f4;
    }

    i {
      font-size: 12px;
      padding: 0;
    }
  }

  &__title {
    margin: 0 0 0 12px;
    font-size: 22px;
    font-weight: 400;
    color: $text;
  }

  &__filter {
    min-width: 280px;
  }

  // Khung lưới: chỉ vẽ 1 đường viền ngoài, các ô dùng border-top/left để không bị nét đôi.
  &__frame {
    border: 1px solid $line;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
  }

  &__weekdays {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  &__weekday {
    padding: 10px 0;
    text-align: center;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: $muted;
    border-bottom: 1px solid $line;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    grid-auto-rows: minmax(140px, auto);
  }

  &__cell {
    padding: 2px 6px 6px;
    border-left: 1px solid $line;
    border-top: 1px solid $line;
    overflow: hidden;

    &:nth-child(-n + 7) {
      border-top: 0;
    }

    &:nth-child(7n + 1) {
      border-left: 0;
    }

    &.is-outside {
      background: #f8f9fa;

      .gcal__daynum {
        color: #bdc1c6;
      }
    }
  }

  &__daynum-wrap {
    display: flex;
    justify-content: center;
    padding: 4px 0 2px;
  }

  &__daynum {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    color: $text;

    &.is-today {
      background: $accent;
      color: #fff;
    }
  }

  &__events {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  // Viền rõ ràng để thấy được ranh giới vùng bấm của từng phòng khám.
  &__chip {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 8px;
    min-height: 32px;
    border: 1px solid $line;
    border-left: 3px solid $accent;
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
    line-height: 18px;
    color: $text;
    text-align: left;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;

    &:hover {
      background: $accent-light;
      border-color: $accent;
      box-shadow: 0 1px 3px rgba(60, 64, 67, 0.3);
    }

    &:active {
      background: $accent-active;
      box-shadow: none;
    }

    &:focus-visible {
      outline: 2px solid $accent;
      outline-offset: 1px;
    }

    .gcal__dot {
      flex: 0 0 8px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: $accent;
    }

    &.is-nearly-full {
      border-left-color: #f9ab00;

      .gcal__dot {
        background: #f9ab00;
      }

      &:hover {
        background: #fef7e0;
        border-color: #f9ab00;
      }
    }

    &.is-full {
      border-left-color: #d93025;

      .gcal__dot {
        background: #d93025;
      }

      &:hover {
        background: #fce8e6;
        border-color: #d93025;
      }
    }

    &.is-empty {
      border-left-color: #9aa0a6;

      .gcal__dot {
        background: #9aa0a6;
      }
    }
  }

  &__chip-name {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }

  &__chip-count {
    flex: 0 0 auto;
    font-weight: 600;
    font-size: 12px;
    padding: 1px 6px;
    border-radius: 10px;
    background: #f1f3f4;
    color: $muted;
  }

  &__detail {
    max-height: 60vh;
    overflow-y: auto;
  }
}
</style>
